import {
  ArrowBackIosNew,
  CalendarMonthOutlined,
  ChevronRight,
  FilterList,
  LocalShippingOutlined,
  PlaceOutlined,
  Search,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { InputBase } from "components/common/Input";
import useDebouncedRemoteFilterSearch from "hooks/useDebouncedRemoteFilterSearch";
import theme from "lib/theme";
import { useMemo, useState } from "react";
import { Location } from "types/location";
import { Pickup, PickupStatusOptions } from "types/pickup";
import { formatDate } from "utils/helpers";

type SubmenuKey = "pickupDate" | "location" | "status";

type PickupsFiltersMenuProps = {
  pickupIds: string[];
  onPickupIdsChange: (ids: string[]) => void;
  pickups: Pickup[];
  locationIds: string[];
  onLocationIdsChange: (ids: string[]) => void;
  locations: Location[];
  statuses: string[];
  onStatusesChange: (statuses: string[]) => void;
  statusOptions: readonly PickupStatusOptions[];
  onClearAll: () => void;
  pickupListItemRef?: (node: HTMLElement | null) => void;
};

const SUBMENU_TITLES: Record<SubmenuKey, string> = {
  pickupDate: "PICKUP DATE",
  location: "LOCATION",
  status: "PICKUP STATUS",
};

const SUBMENU_SEARCH_PLACEHOLDERS: Record<SubmenuKey, string> = {
  pickupDate: "Search pickup ID...",
  location: "Search location...",
  status: "Search pickup status...",
};

const menuItemSx = { py: 1.5, px: 2, gap: 1.5, minWidth: 280 };
const optionItemSx = { py: 0, px: 2, gap: 1.5, alignItems: "center" };

function toggleValue(values: string[], value: string) {
  return values.includes(value) ? values.filter((currentValue) => currentValue !== value) : [...values, value];
}

function selectionSecondary(selectedCount: number, singleLabel?: string | number) {
  if (selectedCount === 0) return undefined;
  if (selectedCount === 1) return singleLabel;
  return `${selectedCount} selected`;
}

export default function PickupsFiltersMenu({
  pickupIds,
  onPickupIdsChange,
  pickups,
  locationIds,
  onLocationIdsChange,
  locations,
  statuses,
  onStatusesChange,
  statusOptions,
  onClearAll,
  pickupListItemRef,
}: PickupsFiltersMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [submenu, setSubmenu] = useState<SubmenuKey | null>(null);
  const [submenuSearch, setSubmenuSearch] = useState("");

  const pickupSearch = useDebouncedRemoteFilterSearch<Pickup>({
    query: submenuSearch,
    enabled: submenu === "pickupDate",
    cancelKey: "pickups-filters-pickup-search",
    path: "/pickups",
    filterColumn: "order_number",
    responseKey: "pickups",
    pageSize: 25,
  });

  const locationSearch = useDebouncedRemoteFilterSearch<Location>({
    query: submenuSearch,
    enabled: submenu === "location",
    cancelKey: "pickups-filters-location-search",
    path: "/locations",
    filterColumn: "name",
    responseKey: "locations",
  });

  const searchQuery = submenuSearch.trim().toLowerCase();
  const remoteSearchPending = pickupSearch.isPending || locationSearch.isPending;
  const showMinCharsHint =
    (submenu === "pickupDate" || submenu === "location") &&
    submenuSearch.trim().length > 0 &&
    submenuSearch.trim().length < pickupSearch.minChars;

  const filteredPickups = useMemo(() => {
    if (showMinCharsHint && submenu === "pickupDate") return [];
    const source = pickupSearch.isRemoteSearch ? pickupSearch.results : pickups;
    return source.filter(({ scheduledAt }) => scheduledAt);
  }, [pickupSearch.isRemoteSearch, pickupSearch.results, pickups, showMinCharsHint, submenu]);

  const filteredLocations = useMemo(() => {
    if (showMinCharsHint && submenu === "location") return [];
    const source = locationSearch.isRemoteSearch ? locationSearch.results : locations;
    return source.filter(({ name }) => Boolean(name?.trim()));
  }, [locationSearch.isRemoteSearch, locationSearch.results, locations, showMinCharsHint, submenu]);

  const filteredStatuses = useMemo(() => {
    if (!searchQuery) return statusOptions;
    return statusOptions.filter(({ label }) => label.toLowerCase().includes(searchQuery));
  }, [searchQuery, statusOptions]);

  const activeFilterCount = Number(pickupIds.length > 0) + Number(locationIds.length > 0) + Number(statuses.length > 0);
  const hasNoMatches =
    !remoteSearchPending &&
    ((submenu === "pickupDate" && filteredPickups.length === 0) ||
      (submenu === "location" && filteredLocations.length === 0) ||
      (submenu === "status" && filteredStatuses.length === 0));

  const resetMenuState = () => {
    pickupSearch.reset();
    locationSearch.reset();
    setSubmenu(null);
    setSubmenuSearch("");
  };

  const handleClose = () => {
    resetMenuState();
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<FilterList />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          whiteSpace: "nowrap",
          borderStyle: "dashed",
          borderColor: theme.palette.text.disabled,
          color: theme.palette.text.primary,
          bgcolor: "#fff",
          height: 37,
          px: 2,
          flexShrink: 0,
          "&:hover": { borderColor: theme.palette.text.primary, bgcolor: "#fff" },
        }}
      >
        Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            minWidth: 300,
            overflow: "hidden",
          },
        }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        {submenu == null ? (
          <>
            <Box sx={{ py: 1 }}>
              <MenuItem sx={menuItemSx} onClick={() => setSubmenu("pickupDate")}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CalendarMonthOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Pickup Date"
                  secondary={selectionSecondary(
                    pickupIds.length,
                    pickups.find(({ id }) => id.toString() === pickupIds[0])?.scheduledAt
                      ? formatDate(pickups.find(({ id }) => id.toString() === pickupIds[0])?.scheduledAt || "")
                      : undefined
                  )}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
              </MenuItem>

              <MenuItem sx={menuItemSx} onClick={() => setSubmenu("location")}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <PlaceOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Location"
                  secondary={selectionSecondary(
                    locationIds.length,
                    locations.find(({ id }) => id?.toString() === locationIds[0])?.name
                  )}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
              </MenuItem>

              <MenuItem sx={menuItemSx} onClick={() => setSubmenu("status")}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <LocalShippingOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Pickup Status"
                  secondary={selectionSecondary(
                    statuses.length,
                    statusOptions.find(({ value }) => value === statuses[0])?.label
                  )}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
              </MenuItem>
            </Box>

            {activeFilterCount > 0 && (
              <>
                <Divider />
                <MenuItem
                  sx={{ ...menuItemSx, color: theme.palette.primary.main, fontWeight: 600 }}
                  onClick={() => {
                    onClearAll();
                    handleClose();
                  }}
                >
                  Clear all filters
                </MenuItem>
              </>
            )}
          </>
        ) : (
          <>
            <Stack direction="row" alignItems="center" gap={0.5} px={1} py={1}>
              <IconButton aria-label="Back to filters" size="small" onClick={resetMenuState}>
                <ArrowBackIosNew sx={{ fontSize: 14 }} />
              </IconButton>
              <Typography
                variant="caption"
                fontWeight={700}
                letterSpacing="0.06em"
                color="text.secondary"
                sx={{ textTransform: "uppercase" }}
              >
                {SUBMENU_TITLES[submenu]}
              </Typography>
            </Stack>

            <Divider />
            <Box px={2} py={0.75}>
              <InputBase
                size="small"
                placeholder={SUBMENU_SEARCH_PLACEHOLDERS[submenu]}
                value={submenuSearch}
                onChange={(event) => setSubmenuSearch(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ".MuiInputBase-input": { fontSize: 14, padding: "0 !important" },
                  fieldset: { border: "none" },
                }}
              />
            </Box>
            <Divider />

            <Box sx={{ maxHeight: 280, overflowY: "auto", py: 0.5 }}>
              {submenu === "pickupDate" && pickupSearch.isPending && (
                <Box px={2} py={2} display="flex" justifyContent="center">
                  <CircularProgress size={20} />
                </Box>
              )}
              {submenu === "pickupDate" &&
                !pickupSearch.isPending &&
                filteredPickups.map((pickup) => {
                  const id = pickup.id.toString();
                  const selected = pickupIds.includes(id);
                  return (
                    <MenuItem
                      key={pickup.id}
                      ref={pickupSearch.isRemoteSearch ? undefined : pickupListItemRef}
                      sx={optionItemSx}
                      selected={selected}
                      onClick={() => onPickupIdsChange(toggleValue(pickupIds, id))}
                    >
                      <Checkbox edge="start" checked={selected} size="small" tabIndex={-1} disableRipple />
                      <ListItemText primary={`${formatDate(pickup.scheduledAt)} - ${pickup.orderNumber}`} />
                    </MenuItem>
                  );
                })}

              {submenu === "location" && locationSearch.isPending && (
                <Box px={2} py={2} display="flex" justifyContent="center">
                  <CircularProgress size={20} />
                </Box>
              )}
              {submenu === "location" &&
                !locationSearch.isPending &&
                filteredLocations.map((location) => {
                  const id = location.id?.toString() ?? "";
                  const selected = locationIds.includes(id);
                  return (
                    <MenuItem
                      key={location.id}
                      sx={optionItemSx}
                      selected={selected}
                      onClick={() => onLocationIdsChange(toggleValue(locationIds, id))}
                    >
                      <Checkbox edge="start" checked={selected} size="small" tabIndex={-1} disableRipple />
                      <ListItemText primary={location.name} />
                    </MenuItem>
                  );
                })}

              {submenu === "status" &&
                filteredStatuses.map(({ label, value }) => {
                  const selected = statuses.includes(value);
                  return (
                    <MenuItem
                      key={value}
                      sx={optionItemSx}
                      selected={selected}
                      onClick={() => onStatusesChange(toggleValue(statuses, value))}
                    >
                      <Checkbox edge="start" checked={selected} size="small" tabIndex={-1} disableRipple />
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}

              {showMinCharsHint && !remoteSearchPending && (
                <Box px={2} py={2}>
                  <Typography variant="body2" color="text.secondary">
                    Enter at least {pickupSearch.minChars} characters to search
                  </Typography>
                </Box>
              )}
              {hasNoMatches && !showMinCharsHint && (
                <Box px={2} py={2}>
                  <Typography variant="body2" color="text.secondary">
                    No matches
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Menu>
    </>
  );
}
